import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Book as BookIcon,
  Build as BuildIcon,
  Business as BusinessIcon,
  ContactMail as ContactMailIcon,
  LaunchRounded as LaunchRoundedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import logo from '../assets/logo2.png';

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadStats() {
      try {
        const definitions = [
          { label: 'Blogs', endpoint: '/blogs', color: 'primary', icon: <BookIcon /> },
          { label: 'Services', endpoint: '/services', color: 'secondary', icon: <BuildIcon /> },
          { label: 'Resources', endpoint: '/resources', color: 'primary', icon: <ArticleIcon /> },
          { label: 'Clients', endpoint: '/clients', color: 'secondary', icon: <BusinessIcon /> },
          { label: 'Contacts', endpoint: '/contacts', color: 'primary', icon: <ContactMailIcon /> },
          { label: 'White Papers', endpoint: '/whitepapers', color: 'secondary', icon: <ArticleIcon /> },
        ];

        const responses = await Promise.allSettled(
          definitions.map((item) => api.get(item.endpoint))
        );

        if (!active) {
          return;
        }

        setStats(
          definitions.map((item, index) => {
            const response = responses[index];
            const items = response.status === 'fulfilled' && Array.isArray(response.value.data)
              ? response.value.data
              : [];

            return {
              ...item,
              value: items.length,
            };
          })
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      active = false;
    };
  }, []);

  const quickActions = useMemo(
    () => [
      { label: 'Create Blog', path: '/blogs/create' },
      { label: 'Add Service', path: '/services/create' },
      { label: 'Add Resource', path: '/resources/create' },
      { label: 'Upload White Paper', path: '/whitepapers/create' },
    ],
    []
  );

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            background: '#FFFFFF',
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip label="Admin workspace" color="secondary" variant="outlined" />
              <Typography variant="h3" sx={{ mt: 2 }}>
                A calm, efficient workspace for content and operations.
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 2, maxWidth: 760, fontSize: '1.02rem', lineHeight: 1.8 }}>
                Review what is live, publish updates, and keep the public experience aligned with the Express backend without extra clutter.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                {quickActions.map((action) => (
                  <Button
                    key={action.path}
                    variant="contained"
                    onClick={() => navigate(action.path)}
                    endIcon={<LaunchRoundedIcon />}
                  >
                    {action.label}
                  </Button>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                component="img"
                src={logo}
                alt="MeGuide Consulting"
                sx={{
                  width: '100%',
                  maxWidth: 210,
                  display: 'block',
                  ml: { xs: 0, md: 'auto' },
                  opacity: 0.92,
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2.5}>
          {(loading ? Array.from({ length: 6 }) : stats).map((item, index) => (
            <Grid item xs={12} sm={6} xl={4} key={item?.label || index}>
              <Paper sx={{ p: 3, borderRadius: 4, height: '100%' }}>
                {loading ? (
                  <Stack spacing={1.5}>
                    <Skeleton variant="rounded" width={44} height={44} />
                    <Skeleton variant="text" width="55%" height={28} />
                    <Skeleton variant="text" width="35%" height={46} />
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2.5,
                        bgcolor: item.color === 'secondary' ? 'secondary.light' : 'primary.main',
                        color: '#fff',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="h4">{item.value}</Typography>
                  </Stack>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </DashboardLayout>
  );
}

export default Dashboard;
